// 一頁的原系統對話框群（trigger ＋ 對話框），FC-4。
//
// ---------------------------------------------------------------------------
// D-3：trigger 必須自報
// ---------------------------------------------------------------------------
// `docs/FUNCTIONAL-CONTRACT.md` 的 D-3 規定：「會開啟對話框的 React 動作元素必須帶
// `data-modal-trigger="<該對話框的 data-ui-id>"`」，而且 React 側 modals 的證據
// **一律由 Playwright 互動擷取取得**（`tests/react/extract-modals.spec.ts`），
// 擷取腳本只認這個屬性、只點 visible 且 enabled 的元素，不得呼叫 open()／setState 繞過。
//
// 所以本元件做兩件事：
//   ① 為每個要畫的對話框畫一顆**真的可以點**的 trigger 鈕，帶 `data-modal-trigger`；
//   ② 點下去才把對話框掛上（條件渲染，唯讀 GET 抽不到 —— 那是正確結果不是缺陷）。
//
// trigger 鈕的文字取自 legacy 那顆 trigger 的可見文字
// （`scripts/resolve-modal-trigger.ts` 解析出來的 `label`，有 raw DOM 行號可追溯），
// 空的時候退回對話框標題／id（`modalTriggerLabel()`）。**不是自己編一個名字。**
//
// ---------------------------------------------------------------------------
// 送出接線（criteria C5-d）
// ---------------------------------------------------------------------------
// 送出鈕帶 `data-effect="create:<pageId>"`，onSubmit 真的呼叫
// `useDataSource().create(pageId, payload)` —— criteria C5-j 要求「只有真的會呼叫
// DataSource 的按鈕才可以帶 data-effect」，這裡兩者是同一顆按鈕的同一條路徑。
// 呼叫端可用 `onSubmitted` 觀察（單元測試用 spy 斷言 `values[name]` 有沒有進 payload）。

import { useCallback, useState } from 'react';
import { LegacyContractModal } from '@/components/layouts/LegacyContractModal';
import { useDialogs } from '@/components/layouts/dialogs/DialogProvider';
import { useDataSource } from '@/mocks';
import {
  type LegacyModalSpec,
  legacyModalsFor,
  modalInitialValues,
  modalTriggerLabel,
  modalUiId,
} from '@/mocks/legacy-modals';

export interface LegacyContractModalsProps {
  pageId: string;
  /** 只畫這個級別的對話框；省略時畫這一頁全部（含 shared／unique）。 */
  level?: LegacyModalSpec['level'];
  /** 覆寫要畫的規格（global／shared 的具名元件用）。 */
  specs?: readonly LegacyModalSpec[];
  /** 送出完成後的回呼（單元測試觀察 payload 用）。 */
  onSubmitted?: (spec: LegacyModalSpec, payload: Record<string, string>) => void;
  /**
   * 批次 45（#11 B5-2）：modal 開啟時的欄位播種（leave-annual students hidden＝表格勾選；
   * 鍵＝欄位 name）。未傳＝零影響。
   */
  fieldSeed?: Record<string, string>;
  /**
   * 批次 45（#11 B5-2）：送出前的驗證鏈（leaveAnnual.jsp:136-165 逐條；回訊息字串＝
   * alert 攔下不送出；null＝通過）。未傳＝直接送出（既有行為）。
   */
  submitGuard?: (spec: LegacyModalSpec, payload: Record<string, string>) => string | null;
}

export function LegacyContractModals({
  pageId,
  level,
  specs,
  onSubmitted,
  fieldSeed,
  submitGuard,
}: LegacyContractModalsProps) {
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  const list = specs ?? legacyModalsFor(pageId, level);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, Record<string, string>>>({});

  const openModal = useCallback((spec: LegacyModalSpec) => {
    setValues((current) =>
      current[spec.key] === undefined
        ? {
            ...current,
            [spec.key]: {
              ...modalInitialValues(spec),
              ...(fieldSeed ?? {}),
            },
          }
        : current,
    );
    setOpenKey(spec.key);
  // fieldSeed 變動時重新開啟要套用新播種（勾選改變→重開 modal 取得新值）。
  }, [fieldSeed]);

  if (list.length === 0) return null;

  return (
    <div data-ui-id={`${pageId}-legacy-modals`} className="flex flex-wrap gap-2">
      {list.map((spec) => {
        const uiId = modalUiId(pageId, spec);
        return (
          <div key={spec.key} className="contents">
            <button
              type="button"
              data-ui-id={`${uiId}-trigger`}
              // D-3：自報「我會開哪一個對話框」。擷取腳本只認這個屬性。
              data-modal-trigger={uiId}
              className="border border-border px-3 py-1 text-[13px]"
              onClick={() => {
                openModal(spec);
              }}
            >
              {modalTriggerLabel(spec)}
            </button>
            <LegacyContractModal
              uiId={uiId}
              resource={pageId}
              spec={spec}
              open={openKey === spec.key}
              values={values[spec.key] ?? {}}
              onChange={(name, next) => {
                setValues((current) => ({
                  ...current,
                  [spec.key]: { ...(current[spec.key] ?? {}), [name]: next },
                }));
              }}
              onClose={() => {
                setOpenKey(null);
              }}
              onSubmit={(payload) => {
                // 批次 45（#11 B5-2）：驗證鏈 guard（回訊息＝alert 攔下不送出）。
                const violation = submitGuard === undefined ? null : submitGuard(spec, payload);
                if (violation !== null) {
                  void dialogs.alert(violation);
                  return;
                }
                // 真的打 DataSource —— data-effect 才不是假動作（criteria C5-j）。
                void dataSource.create(pageId, payload);
                onSubmitted?.(spec, payload);
              }}
            />
          </div>
        );
      })}
    </div>
  );
}
